/**
 * Authorizations Tab Component for Client Detail Page
 * Displays authorizations with payer info, limits, and actions
 */
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, ClipboardList, Calendar } from "lucide-react";

const SERVICE_TYPE_LABELS = {
  personal_care: "Personal Care",
  skilled_nursing: "Skilled Nursing",
  companion_care: "Companion Care",
  respite_care: "Respite Care",
  home_health_aide: "Home Health Aide",
};

const PAYER_TYPE_LABELS = {
  medicaid: "Medicaid",
  medicare: "Medicare",
  private_pay: "Private Pay",
  insurance: "Insurance",
  va: "VA Benefits",
  waiver: "Waiver Program",
};

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-amber-100 text-amber-700",
  expired: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-700",
};

export function ServiceOrdersTab({
  serviceOrders,
  onAddClick,
  onEditClick,
  onDelete,
  onClearOrphanedSchedules
}) {
  const getServiceTypeLabel = (type) => SERVICE_TYPE_LABELS[type] || type;
  const getPayerTypeLabel = (type) => PAYER_TYPE_LABELS[type] || type;
  const getStatusColor = (status) => STATUS_COLORS[status] || "bg-slate-100 text-slate-700";

  return (
    <div data-testid="client-authorizations-tab">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Authorizations</h3>
            <p className="text-sm text-slate-500">
              Define what services are authorized for this client by payer, with limits for scheduling, billing, and payroll
            </p>
          </div>
          <div className="flex gap-2">
            {onClearOrphanedSchedules && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid="clear-orphaned-schedules-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Clear Orphaned Schedules
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear Orphaned Schedules?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove all auto-generated appointments that belong to deleted authorizations. 
                      Manually created appointments will not be affected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={onClearOrphanedSchedules}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Clear Schedules
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button
              onClick={onAddClick}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="add-authorization-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Authorization
            </Button>
          </div>
        </div>

        {/* Authorizations List */}
        {serviceOrders.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-200">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No authorizations defined</p>
            <p className="text-sm text-slate-400 mt-1">
              Add an authorization to define allowed services for this client
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {serviceOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                data-testid={`authorization-${order.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-slate-800">
                        {getServiceTypeLabel(order.service_type)}
                      </h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 mb-3">
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400">Payer:</span>
                        <span className="font-medium">{getPayerTypeLabel(order.payer_type)}</span>
                        {order.payer_name && <span className="text-slate-500">({order.payer_name})</span>}
                      </div>
                      {order.authorization_number && (
                        <div className="flex items-center gap-1">
                          <span className="text-slate-400">Auth #:</span>
                          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                            {order.authorization_number}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span>{order.start_date} - {order.end_date}</span>
                      </div>
                    </div>

                    {/* Limits Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 bg-slate-50 rounded-lg p-3">
                      {order.hours_per_week && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Hours/Week</p>
                          <p className="font-semibold text-teal-600">{order.hours_per_week}</p>
                        </div>
                      )}
                      {order.hours_per_day && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Hours/Day</p>
                          <p className="font-semibold text-teal-600">{order.hours_per_day}</p>
                        </div>
                      )}
                      {order.total_authorized_hours && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Total Auth Hours</p>
                          <p className="font-semibold text-teal-600">
                            {order.hours_used || 0} / {order.total_authorized_hours}
                          </p>
                        </div>
                      )}
                      {order.visits_per_week && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Visits/Week</p>
                          <p className="font-semibold text-teal-600">{order.visits_per_week}</p>
                        </div>
                      )}
                      {order.billing_rate && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Billing Rate</p>
                          <p className="font-semibold text-emerald-600">${order.billing_rate}</p>
                        </div>
                      )}
                      {order.payroll_rate && (
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Payroll Rate</p>
                          <p className="font-semibold text-blue-600">${order.payroll_rate}</p>
                        </div>
                      )}
                    </div>

                    {order.notes && (
                      <p className="text-sm text-slate-500 mt-3 italic">&quot;{order.notes}&quot;</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditClick(order)}
                      data-testid={`edit-authorization-${order.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-authorization-${order.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Authorization?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this authorization. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(order.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
